import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { analytics } from './firebase/config';
import { logEvent } from 'firebase/analytics';
import VisitorAnalyticsService from './services/visitorAnalyticsService';
import NewHeader from './components/NewHeader';
import Topbar from './components/Topbar';
import NewHeroSection from './components/NewHeroSection';
import CategorySection from './components/CategorySection';
import WhyChooseUs from './components/WhyChooseUs';
import BeforeAfterSection from './components/BeforeAfterSection';
import ProductNew from './components/ProductNew';
import FAQ from './components/FAQ';
import TestimonialsSection from './components/TestimonialsSection';
import TextEffectSection from './components/TextEffectSection';
import Footer from './components/Footer';
import LaptopsPage from './pages/LaptopsPage';
import MonitorsPage from './pages/MonitorsPage';
import DesktopsPage from './pages/DesktopsPage';
import MiniPCsPage from './pages/MiniPCsPage';
import AllInOnePage from './pages/AllInOnePage';
import ProductPage from './pages/ProductPage';
import MonitorProductPage from './pages/MonitorProductPage';
import DesktopProductPage from './pages/DesktopProductPage';
import MiniPCProductPage from './pages/MiniPCProductPage';
import AllInOneProductPage from './pages/AllInOneProductPage';
import AccessoriesPage from './pages/AccessoriesPage';
import AccessoryProductPage from './pages/AccessoryProductPage';
import AllProductsPage from './pages/AllProductsPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ProfilePage from './pages/ProfilePage';
import OrdersPage from './pages/OrdersPage';
import OrderConfirmationPage from './pages/OrderConfirmationPage';
import UserSettingsPage from './pages/UserSettingsPage';
import StorePage from './pages/StorePage';
import BusinessPage from './pages/BusinessPage';
import WarrantyPage from './pages/WarrantyPage';
import ShippingPage from './pages/ShippingPage';
import PrivacyPage from './pages/PrivacyPage';
import HelpCenterPage from './pages/HelpCenterPage';
import AboutUsPage from './pages/AboutUsPage'; // Import the new AboutUsPage
import BlogPage from './pages/BlogPage';
import ContactUsPage from './pages/ContactUsPage';
import TermsAndConditionsPage from './pages/TermsAndConditionsPage';
import RefundPolicyPage from './pages/RefundPolicyPage';
import AdminAnalyticsPage from './admin/analytics/AdminAnalyticsPage';
import ProtectedRoute from './components/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop'; // Import ScrollToTop
import GlobalPopups from './components/GlobalPopups';
import StickySidebarCTA from './components/StickySidebarCTA';
import WhatIsRenewed from './components/WhatIsRenewed';
import FeaturedProduct2 from './components/FeaturedProduct2';
import FeaturedImage from './components/FeaturedImage';
import CartPopup from './components/CartPopup';
import Preloader from './components/Preloader';
import ScrollImage from './components/ScrollImage';
import './App.css';

const PageViewTracker = () => {
  const location = useLocation();

  useEffect(() => {
    // Initialize analytics on first load
    VisitorAnalyticsService.initializeAnalytics();
  }, []);

  useEffect(() => {
    // Track page views in Firebase Analytics
    if (analytics) {
      logEvent(analytics, 'page_view', {
        page_path: location.pathname + location.search,
      });
    }

    // Track enhanced visitors and page views in Firestore
    trackEnhancedVisitorAndPageView();
  }, [location]);

  // Function to track enhanced visitors and page views in Firestore
  const trackEnhancedVisitorAndPageView = async () => {
    try {
      // Track daily visitor with enhanced data
      await VisitorAnalyticsService.trackDailyVisitor({
        page: location.pathname + location.search,
        source: 'organic' // You can customize this based on referrer analysis
      });

      // Track detailed page view with performance metrics
      await VisitorAnalyticsService.trackPageView(location.pathname + location.search, {
        pageTitle: document.title,
        previousPage: document.referrer
      });
    } catch (error) {
      console.error('Error tracking visitor/page view:', error);
      // Track the error itself
      VisitorAnalyticsService.trackError(error, {
        context: 'PageViewTracker',
        action: 'trackEnhancedVisitorAndPageView'
      });
    }
  };

  return null;
};

// Component to conditionally render Topbar
const ConditionalTopbar = () => {
  const location = useLocation();
  // Show Topbar ONLY on the homepage
  if (location.pathname !== '/') {
    return null;
  }
  return <Topbar />;
};

// Component to conditionally render Footer
const ConditionalFooter = () => {
  const location = useLocation();

  // Hide footer on product pages for mobile
  const isProductPage = location.pathname.match(/^\/(product|products|monitor|desktop|mini-pc|all-in-one-pc|accessory)(\/|$)/);
  const isMobile = window.innerWidth <= 1024; // Consider tablet and mobile

  if (isProductPage && isMobile) {
    return null;
  }

  return <Footer />;
};

function App() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // If not on home page, disable loading immediately
    if (window.location.pathname !== '/') {
      setLoading(false);
    }
  }, []);

  return (
    <HelmetProvider>
      <AuthProvider>
        <CartProvider>
          {loading && <Preloader onComplete={() => setLoading(false)} />}
          <Router>
            <PageViewTracker />
            <ScrollToTop />
            <div className="App">
              <Routes>
                <Route path="/login" element={
                  <>
                    <NewHeader />
                    <main>
                      <LoginPage />
                    </main>
                    <ConditionalFooter />
                  </>
                } />
                <Route path="/signup" element={
                  <>
                    <NewHeader />
                    <main>
                      <SignupPage />
                    </main>
                    <ConditionalFooter />
                  </>
                } />
                <Route path="/*" element={
                  <>
                    <ConditionalTopbar />
                    <NewHeader />
                    <main>
                      <Routes>
                        <Route path="/" element={
                          <>
                            {/* <HeroSection /> */}
                            <NewHeroSection />
                            <WhatIsRenewed />
                            <ProductNew />
                            <FeaturedImage />
                            <CategorySection />
                            <FeaturedProduct2 onAddToCart={() => { }} />
                            <ScrollImage />
                            <FAQ />
                            <WhyChooseUs />
                            <BeforeAfterSection />
                            <TestimonialsSection />
                            <TextEffectSection />
                            {/* Firebase Explorer - Remove this in production */}
                          </>
                        } />
                        <Route path="/laptops" element={<LaptopsPage />} />
                        <Route path="/monitors" element={<MonitorsPage />} />
                        <Route path="/desktops" element={<DesktopsPage />} />
                        <Route path="/mini-pcs" element={<MiniPCsPage />} />
                        <Route path="/all-in-one" element={<AllInOnePage />} />
                        <Route path="/accessories" element={<AccessoriesPage />} />
                        <Route path="/products" element={<AllProductsPage />} />
                        <Route path="/product/:productId" element={<ProductPage />} />
                        <Route path="/products/:slug" element={<ProductPage />} />
                        <Route path="/monitor/:productId" element={<MonitorProductPage />} />
                        <Route path="/desktop/:productId" element={<DesktopProductPage />} />
                        <Route path="/mini-pc/:productId" element={<MiniPCProductPage />} />
                        <Route path="/all-in-one-pc/:productId" element={<AllInOneProductPage />} />
                        <Route path="/accessory/:productId" element={<AccessoryProductPage />} />
                        <Route path="/cart" element={<CartPage />} />
                        <Route path="/checkout" element={<CheckoutPage />} />
                        <Route path="/orders" element={<OrdersPage />} />
                        <Route path="/order-confirmation/:orderId" element={<OrderConfirmationPage />} />
                        <Route path="/profile" element={
                          <ProtectedRoute>
                            <ProfilePage />
                          </ProtectedRoute>
                        } />
                        <Route path="/settings" element={
                          <ProtectedRoute>
                            <UserSettingsPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/store" element={<StorePage />} />
                        <Route path="/business" element={<BusinessPage />} />
                        <Route path="/warranty" element={<WarrantyPage />} />
                        <Route path="/shipping" element={<ShippingPage />} />
                        <Route path="/privacy" element={<PrivacyPage />} />
                        <Route path="/help-center" element={<HelpCenterPage />} />
                        <Route path="/about-us" element={<AboutUsPage />} /> {/* Add the new About Us route */}
                        <Route path="/blog" element={<BlogPage />} />
                        <Route path="/blog/:slug" element={<BlogPage />} />
                        <Route path="/contact-us" element={<ContactUsPage />} />
                        <Route path="/terms" element={<TermsAndConditionsPage />} />
                        <Route path="/refund-policy" element={<RefundPolicyPage />} />
                        <Route path="/admin/analytics" element={<AdminAnalyticsPage />} />

                      </Routes>
                    </main>
                    <ConditionalFooter />
                  </>
                } />
              </Routes>
            </div>
            <GlobalPopups />
            <CartPopup />
            <StickySidebarCTA />
          </Router>
        </CartProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
